import { useTranslation } from 'react-i18next'
import ConfirmDialog, { confirmDialog } from '../../../components/ui/ConfirmDialog'
import AttachmentTable, { type AttachmentTableRow } from '../../../components/ui/AttachmentTable'
import { GRANTS } from '../../../auth/grants'
import FileUpload from '../../../components/ui/FileUpload'
import { useToast } from '../../../components/ui/ToastProvider'
import { useDeleteAttachment, useUploadAttachments } from '../hooks/useActionDetail'
import type { ActionDocument } from '../types'
import { documentMutationErrorKey } from '../statusError'
import { DOCUMENT_ACCEPT } from '../../../lib/uploadAccept'

interface Props {
  actionId: number
  documents: ActionDocument[]
  /** true → yükleme + kaldırma kontrolleri; false → yalnız indirme (İptal'de de indirilebilir, 7.18). */
  editable: boolean
}

/**
 * Aksiyon Detay — Dokümanlar bölümü (7.9). Ortak {@link AttachmentTable} ile <b>Dosya Adı · Tür · Boyut ·
 * Yükleyen · Yüklenme Tarihi</b> + satır bazında <b>İndir</b> ve (yetkili) <b>Kaldır</b> (onaylı, soft-delete).
 * Yükleme anında kaydeder (ACTION_FILE tipi); değişiklikte detay tazelenir (Toplantı Detay Tutanaklar kalıbı).
 */
export default function ActionDocumentsSection({ actionId, documents, editable }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()

  const uploadMut = useUploadAttachments(actionId)
  const deleteMut = useDeleteAttachment(actionId)
  const busy = uploadMut.isPending || deleteMut.isPending

  const handleAdd = (files: File[]) => {
    if (files.length === 0) return
    uploadMut.mutate(files, {
      onSuccess: () => toast.success(t('actions.detail.documents.uploaded')),
      onError: (e) =>
        toast.error(t(documentMutationErrorKey(e, 'actions.detail.documents.uploadError'))),
    })
  }

  const confirmRemove = (row: AttachmentTableRow) =>
    confirmDialog({
      group: 'action-doc-remove',
      header: t('common.removeFileConfirm.title'),
      message: t('common.removeFileConfirm.message', { name: row.fileName }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('common.delete'),
      accept: () =>
        deleteMut.mutate(Number(row.key), {
          onSuccess: () => toast.success(t('actions.detail.documents.removed')),
          onError: (e) =>
            toast.error(t(documentMutationErrorKey(e, 'actions.detail.documents.removeError'))),
        }),
    })

  const rows: AttachmentTableRow[] = documents.map((d) => ({
    key: String(d.id),
    fileName: d.fileName,
    extension: d.extension,
    size: d.size,
    downloadId: d.id,
    uploadedByName: d.uploadedByName,
    uploadedAt: d.uploadedAt,
  }))

  return (
    <section className="minutes-section">
      <div className="minutes-section-head">
        <h2>{t('actions.detail.documents.title')}</h2>
      </div>

      <AttachmentTable
        rows={rows}
        emptyText={t('actions.detail.documents.empty')}
        onRemove={editable ? confirmRemove : undefined}
        busy={busy}
        // Bağlam: ATTACHED_FILE.TABLE_NAME = 'MEETING_MINUTES' (aksiyon dokümanları).
        requiredGrant={GRANTS.MEETING_FILE_DOWNLOAD}
      />

      {editable && (
        <div className="mtg-doc-upload">
          <FileUpload
            files={[]}
            onChange={handleAdd}
            accept={DOCUMENT_ACCEPT}
            label={busy ? t('actions.detail.documents.uploading') : t('actions.detail.documents.add')}
            hint={t('actions.detail.documents.hint')}
          />
        </div>
      )}

      {editable && <ConfirmDialog group="action-doc-remove" />}
    </section>
  )
}
